<template>
  <div>
    <canvas width="800" height="250"></canvas>

    <PublicationBreakdownModal
        v-if="period"
        :period="period"
        :portal="portal"
        :user="user"
        :granularity="granularity"
        metric="publication_ad_value_after_costs"
        :fixed-filters="[{dimension: 'publication_based_channel_id', operator: '=', value: publicationBasedChannel.id}]"
    />
  </div>
</template>

<script>
import datalabels from 'chartjs-plugin-datalabels'
import moment from 'moment'
import Chart from 'chart.js'
import PublicationBreakdownModal from '@/components/Modals/Administration/Portals/PublicationBreakdownModal'

const totalizer = {
  id: 'totalizer',

  beforeUpdate: chart => {
    let totals = {}
    let utmost = 0

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (chart.isDatasetVisible(datasetIndex)) {
        utmost = datasetIndex

        dataset.data.forEach((value, index) => {
          totals[index] = (totals[index] || 0) + (datasetIndex > 0 ? value : 0)
        })
      }
    })

    chart.$totalizer = {
      totals: totals,
      utmost: utmost
    }
  }
}

export default {
  components: {PublicationBreakdownModal},

  props: ['year', 'user', 'portal', 'publicationBasedChannel', 'granularity', 'targetMetric', 'rows', 'loading'],

  mounted() {
    this.$nextTick(this.draw)
  },

  watch: {
    loading() {
      this.$nextTick(this.draw)
    },
  },

  data() {
    return {
      period: null,
      chart: null
    }
  },

  computed: {
    targetDataset() {
      return {
        label: 'Target',
        data: this.targets,
        type: 'line',
        backgroundColor: '#888888',
        borderColor: '#888888',
        fill: false
      }
    },

    targets() {
      return this.periods.map(period => {
        return this.rows
            .filter(row => row[this.granularityDimension] === period.format(this.granularityFormat))
            .reduce((result, row) => result + (row[this.targetMetric] || 0), 0)
      }, {})
    },

    channelDataset() {
      return {
        label: 'Value',
        data: this.periods.map(period => {
          return this.rows
              .filter(row => row[this.granularityDimension] === period.format(this.granularityFormat))
              .reduce((result, row) => result + (row.publication_ad_value_after_costs || 0), 0)
        }, {}),
        backgroundColor: this.publicationBasedChannel.attributes.color
      }
    },

    periods() {
      let startDate = moment().year(this.year).startOf('year')

      let endDate = moment().year(this.year).endOf('year')

      let result = []

      while (startDate.isBefore(endDate)) {
        result.push(startDate.clone())

        startDate.add(this.granularityLength, 'month')
      }

      return result
    },

    labels() {
      return this.periods.map(period => period.format(this.granularityFormat))
    },

    granularityDimension() {
      if (this.granularity === 'months') {
        return 'publish_month'
      }

      if (this.granularity === 'quarters') {
        return 'publish_quarter'
      }

      return 'publish_year'
    },

    granularityLength() {
      if (this.granularity === 'months') {
        return 1
      }

      if (this.granularity === 'quarters') {
        return 3
      }

      return 12
    },

    granularityFormat() {
      if (this.granularity === 'months') {
        return 'MM/YYYY'
      }

      if (this.granularity === 'quarters') {
        return '[Q]Q/YYYY'
      }

      return 'YYYY'
    }
  },

  methods: {
    draw() {
      if (this.chart) {
        this.chart.destroy()
      }

      this.chart = new Chart(this.$el.querySelector('canvas').getContext('2d'), {
        plugins: [datalabels, totalizer],

        type: 'bar',

        data: {
          labels: this.labels,
          datasets: [
            this.targetDataset,
            this.channelDataset
          ]
        },

        options: {
          scales: {
            xAxes: [{
              stacked: true
            }],
            yAxes: [{
              stacked: true,
              ticks: {
                beginAtZero: true,
                callback: value => this.$options.filters.price(value)
              }
            }]
          },

          legend: {
            position: 'bottom'
          },

          layout: {
            padding: {
              top: 25
            }
          },

          plugins: {
            datalabels: {
              anchor: 'end',
              align: 'end',
              formatter: (value, ctx) => this.$options.filters.price(ctx.chart.$totalizer.totals[ctx.dataIndex]),
              display: (ctx) => ctx.datasetIndex === ctx.chart.$totalizer.utmost
            }
          },

          tooltips: {
            callbacks: {
              label: (t, d) => d.datasets[t.datasetIndex].label + ': ' + this.$options.filters.price(d.datasets[t.datasetIndex].data[t.index])
            }
          },

          onClick: (event, elements) => {
            if (elements.length) {
              this.period = this.periods[elements[0]['_index']]

              this.$nextTick(() => $('#publication-breakdown-modal').modal('show'))
            }
          }
        }
      });
    }
  }
}
</script>
